// Light / dark colour scheme for the console.
//
// The palette itself was never the missing piece: `index.css` has carried a `.dark`
// token block since the scaffold, and tailwind runs `darkMode: ['class']` (the Nexa
// brand pack, src/theme/nexa.brand.json, colours the light scheme only). What was
// missing is the switch — nothing ever put `.dark` on
// <html>, so all of that was unreachable and an operator on a dark-preferring OS got
// a dark boot splash that flashed into a white console.
//
// This module is that switch, and nothing else. It is DOM-only and framework-free so
// the same three functions serve the React hook, the unit tests, and the pre-hydration
// inline script in index.html (which is hand-written to match `applyScheme` — see the
// comment there; keep the two in step).
//
// Three preferences, not two: `system` is the default and follows the OS live, which
// is what an operator working a night shift expects. `light`/`dark` are an explicit
// override that outlives the tab.

/** What the operator chose. `system` follows the OS and keeps following it. */
export type ThemePreference = 'system' | 'light' | 'dark';

/** What that resolves to right now — the two schemes the tokens actually define. */
export type ColorScheme = 'light' | 'dark';

/** Namespaced like `nexa.print.format` / `nexa.airport-scope`. */
export const THEME_STORAGE_KEY = 'nexa.theme';

export const DARK_QUERY = '(prefers-color-scheme: dark)';

const PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (PREFERENCES as readonly string[]).includes(value);
}

/**
 * The stored preference, or `system` when there is none / it is unreadable.
 * Storage throws in a locked-down browser (Safari private mode, third-party-cookie
 * blocking); a console that fails to boot over a colour preference would be absurd.
 */
export function readPreference(): ThemePreference {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(raw) ? raw : 'system';
  } catch {
    return 'system';
  }
}

export function writePreference(preference: ThemePreference): void {
  try {
    if (preference === 'system') window.localStorage.removeItem(THEME_STORAGE_KEY);
    else window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    /* preference is in-memory only this session */
  }
}

/** What the OS is asking for. Defaults to light where matchMedia is absent (jsdom). */
export function systemScheme(): ColorScheme {
  try {
    return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function resolveScheme(preference: ThemePreference): ColorScheme {
  return preference === 'system' ? systemScheme() : preference;
}

/**
 * Put the scheme on <html>.
 *
 * `.dark` is what tailwind and the brand packs key on (`html:root.dark`). The
 * `color-scheme` property is the second half and is easy to forget: it is what makes
 * the browser paint scrollbars, form controls and the canvas behind the page in the
 * matching scheme. Without it a dark console keeps white scrollbars and a white
 * overscroll gutter. `data-theme` carries the *preference* for CSS/tests that want to
 * distinguish "dark because the OS says so" from "dark because the operator said so".
 */
export function applyScheme(scheme: ColorScheme, preference: ThemePreference): void {
  const root = document.documentElement;
  root.classList.toggle('dark', scheme === 'dark');
  root.style.colorScheme = scheme;
  root.dataset.theme = preference;
}
